import { useState } from "react";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";
import {
  TrendingUp, Clock, CheckCircle2, MessageSquare, Smartphone, Mail,
  MessageCircle, Users, Award, ShieldCheck
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const DAILY_DATA = [
  { day: "Mon", inbound: 124, outbound: 152 },
  { day: "Tue", inbound: 158, outbound: 184 },
  { day: "Wed", inbound: 142, outbound: 176 },
  { day: "Thu", inbound: 189, outbound: 215 },
  { day: "Fri", inbound: 204, outbound: 240 },
  { day: "Sat", inbound: 85, outbound: 92 },
  { day: "Sun", inbound: 62, outbound: 68 }
];

const AGENT_LEADERBOARD = [
  { name: "Master Admin", role: "Super Admin", handled: 512, avgTime: "2m 14s", rating: "4.95 ★", status: "Active" },
  { name: "AI Sales Copilot", role: "Autonomous Agent", handled: 724, avgTime: "0m 18s", rating: "4.88 ★", status: "Autonomous" },
  { name: "Sarah Jenkins", role: "Senior Account Exec", handled: 182, avgTime: "3m 42s", rating: "4.92 ★", status: "Active" },
  { name: "Direct Client Desk", role: "Tier 1 Support", handled: 64, avgTime: "4m 10s", rating: "4.79 ★", status: "Offline" }
];

export default function InboxAnalyticsTab() {
  const [dateRange, setDateRange] = useState("7d");

  return (
    <div className="flex-1 h-full overflow-y-auto bg-slate-50 dark:bg-slate-950 p-6 font-sans">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              Conversation Analytics & SLAs
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Response SLAs, omnichannel conversation volume, and agent performance breakdown.
            </p>
          </div>
          <div className="flex items-center gap-1 bg-white dark:bg-slate-900 p-1 rounded-lg border border-slate-200 dark:border-slate-800 text-xs">
            {["24h", "7d", "30d", "90d"].map((r) => (
              <button
                key={r}
                onClick={() => setDateRange(r)}
                className={`px-3 py-1 rounded font-medium transition-colors ${
                  dateRange === r
                    ? "bg-blue-600 text-white shadow-xs"
                    : "text-slate-500 hover:text-slate-900 dark:hover:text-slate-200"
                }`}
              >
                {r.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Top KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between text-slate-500 text-xs">
              <span className="font-semibold uppercase tracking-wider">Avg First Response</span>
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-2">3m 12s</div>
            <div className="text-xs text-emerald-600 font-medium mt-1">↓ 42s faster than goal</div>
          </Card>

          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between text-slate-500 text-xs">
              <span className="font-semibold uppercase tracking-wider">SLA Met (&lt; 5m)</span>
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="text-2xl font-bold text-emerald-600 mt-2">98.6%</div>
            <div className="text-xs text-slate-400 mt-1">Exceeds 95% target</div>
          </Card>

          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between text-slate-500 text-xs">
              <span className="font-semibold uppercase tracking-wider">Total Messages</span>
              <MessageSquare className="w-4 h-4 text-purple-600" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-2">1,482</div>
            <div className="text-xs text-blue-600 font-medium mt-1">↑ 18.4% this week</div>
          </Card>

          <Card className="p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between text-slate-500 text-xs">
              <span className="font-semibold uppercase tracking-wider">Resolution Rate</span>
              <CheckCircle2 className="w-4 h-4 text-amber-500" />
            </div>
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-2">94.2%</div>
            <div className="text-xs text-slate-400 mt-1">First contact resolved</div>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Volume Bar Chart */}
          <Card className="lg:col-span-2 p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                  Message Volume & Activity (Inbound vs Outbound)
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">Daily volume processed across SMS, Email, and Web chat</p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-600" /> Inbound
                </span>
                <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-400" /> Outbound
                </span>
              </div>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={DAILY_DATA} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#0A0F29",
                      borderRadius: "8px",
                      border: "none",
                      color: "#fff",
                      fontSize: "12px"
                    }}
                  />
                  <Bar dataKey="inbound" fill="#0044FF" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="outbound" fill="#818cf8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Channel Share */}
          <Card className="p-5 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 flex flex-col justify-between">
            <div>
              <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                Channel Distribution
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Breakdown of communication traffic</p>

              <div className="space-y-4 mt-6">
                <div>
                  <div className="flex items-center justify-between text-xs font-medium mb-1">
                    <span className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                      <Smartphone className="w-3.5 h-3.5 text-blue-600" /> SMS / Text
                    </span>
                    <span className="font-bold text-blue-600">54% (802)</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: "54%" }} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-xs font-medium mb-1">
                    <span className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                      <Mail className="w-3.5 h-3.5 text-purple-600" /> Email Threads
                    </span>
                    <span className="font-bold text-purple-600">31% (459)</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: "31%" }} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-xs font-medium mb-1">
                    <span className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                      <MessageCircle className="w-3.5 h-3.5 text-emerald-600" /> WhatsApp
                    </span>
                    <span className="font-bold text-emerald-600">15% (221)</span>
                  </div>
                  <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2">
                    <div className="bg-emerald-600 h-2 rounded-full" style={{ width: "15%" }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950/40 p-3 rounded-lg border border-blue-200 dark:border-blue-800 mt-6 text-xs text-blue-900 dark:text-blue-200">
              <span className="font-semibold">Insight:</span> SMS response times are 4x faster than Email. Automated AI responses handle 48% of after-hours traffic.
            </div>
          </Card>
        </div>

        {/* Agent Leaderboard */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                Agent & AI Performance Leaderboard
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">Handled volume, response velocity, and CSAT scores</p>
            </div>
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 text-xs">
              Active Team
            </Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 text-slate-500 uppercase tracking-wider font-semibold">
                <tr>
                  <th className="p-3.5">Agent / Operator</th>
                  <th className="p-3.5">Role</th>
                  <th className="p-3.5 text-center">Messages Handled</th>
                  <th className="p-3.5 text-center">Avg First Response</th>
                  <th className="p-3.5 text-center">CSAT Rating</th>
                  <th className="p-3.5 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {AGENT_LEADERBOARD.map((ag) => (
                  <tr key={ag.name} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                    <td className="p-3.5 font-semibold text-slate-900 dark:text-slate-100">
                      {ag.name}
                    </td>
                    <td className="p-3.5 text-slate-500">
                      {ag.role}
                    </td>
                    <td className="p-3.5 text-center font-bold text-slate-800 dark:text-slate-200">
                      {ag.handled}
                    </td>
                    <td className="p-3.5 text-center font-mono text-blue-600">
                      {ag.avgTime}
                    </td>
                    <td className="p-3.5 text-center font-medium text-amber-600">
                      {ag.rating}
                    </td>
                    <td className="p-3.5 text-right">
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${
                          ag.status === "Active" || ag.status === "Autonomous"
                            ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                            : "bg-slate-100 text-slate-500 border-slate-200"
                        }`}
                      >
                        ● {ag.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

